import EinaSemiBold from '~Assets/Fonts/Eina01SemiBold.woff2';
import EinaBold from '~Assets/Fonts/Eina01Bold.woff2';

/**
 * Fonts for image prints.
 *
 * The receipt is rendered on a canvas, and canvas text uses whatever fonts the device has.
 * Adyen terminals ship a thin typewriter fallback that prints faintly, so we register our
 * own brand font (Eina, already bundled for the UI) through the FontFace API before drawing.
 * Two weights are enough for a receipt: semibold for body copy, bold for emphasis. Regular
 * weights are deliberately not used; they are too light for 203 dpi thermal paper.
 */

export const RECEIPT_FONT_FAMILY = 'EinaReceipt';

const FALLBACK_STACK = '"Helvetica Neue", Helvetica, Roboto, Arial, sans-serif';

export type ReceiptFontWeight = 600 | 700;

let loading: Promise<boolean> | undefined;

/** Registers the receipt fonts once. Resolves false if the device cannot load them. */
export const loadReceiptFonts = (): Promise<boolean> => {
  if (loading) {
    return loading;
  }

  loading = (async () => {
    if (typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts) {
      return false;
    }

    try {
      const faces = [
        new FontFace(RECEIPT_FONT_FAMILY, `url(${EinaSemiBold})`, { weight: '600' }),
        new FontFace(RECEIPT_FONT_FAMILY, `url(${EinaBold})`, { weight: '700' }),
      ];

      const loaded = await Promise.all(faces.map((face) => face.load()));
      loaded.forEach((face) => document.fonts.add(face));

      return true;
    } catch (e) {
      return false;
    }
  })();

  return loading;
};

/** CSS font-family list to use on the canvas, given whether the brand font loaded. */
export const receiptFontStack = (brandFontLoaded: boolean) =>
  brandFontLoaded ? `"${RECEIPT_FONT_FAMILY}", ${FALLBACK_STACK}` : FALLBACK_STACK;
